import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLang } from '../context/LanguageContext';
import './Navbar.css';

const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const { lang, switchLang, t } = useLang();
  const [scrolled, setScrolled]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [langOpen, setLangOpen]   = useState(false);
  const langRef = useRef(null);

  /* scroll shadow */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* close lang dropdown on outside click */
  useEffect(() => {
    const onClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  /* lock body scroll when mobile menu is open */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  /* close menu on route change (via NavLink onClick) */
  const closeMenu = () => setMenuOpen(false);

  const navItems = [
    { to: '/',          label: t.nav.home      },
    { to: '/tutorials', label: t.nav.tutorials  },
    { to: '/projects',  label: t.nav.projects   },
    { to: '/ebooks',    label: t.nav.ebooks     },
    { to: '/about',     label: t.nav.about      },
    { to: '/contact',   label: t.nav.contact    },
  ];

  return (
    <>
      {/* ── NAVBAR BAR ── */}
      <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
        <div className="navbar-container">

          {/* Logo */}
          <Link to="/" className="navbar-logo" onClick={closeMenu}>
            <span className="logo-text">
              Tech By<span className="logo-accent"> WebCoder</span>
            </span>
          </Link>

          {/* Desktop links */}
          <ul className="navbar-links">
            {navItems.map(item => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Right controls */}
          <div className="navbar-controls">

            {/* Language dropdown – desktop only */}
            <div className="lang-switcher desktop-only" ref={langRef}>
              <button
                className="lang-btn"
                onClick={() => setLangOpen(prev => !prev)}
                aria-label="Switch language"
                aria-expanded={langOpen}
              >
                <span className="lang-flag">{lang === 'en' ? '🇺🇸' : '🇮🇳'}</span>
                <span className="lang-code">{lang.toUpperCase()}</span>
                <span className={`lang-arrow ${langOpen ? 'open' : ''}`}>▾</span>
              </button>

              {langOpen && (
                <div className="lang-dropdown animate-scaleIn">
                  <button
                    className={`lang-option ${lang === 'en' ? 'active' : ''}`}
                    onClick={() => { switchLang('en'); setLangOpen(false); }}
                  >
                    🇺🇸 English
                  </button>
                  <button
                    className={`lang-option ${lang === 'hi' ? 'active' : ''}`}
                    onClick={() => { switchLang('hi'); setLangOpen(false); }}
                  >
                    🇮🇳 हिंदी
                  </button>
                </div>
              )}
            </div>

            {/* Theme toggle */}
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>

            {/* Hamburger – mobile only */}
            <button
              className={`hamburger ${menuOpen ? 'open' : ''}`}
              onClick={() => setMenuOpen(prev => !prev)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              <span />
              <span />
              <span />
            </button>

          </div>
        </div>
      </nav>

      {/* ── MOBILE DRAWER (rendered OUTSIDE <nav> so z-index stacks correctly) ── */}
      <div
        className={`mobile-menu ${menuOpen ? 'open' : ''}`}
        aria-hidden={!menuOpen}
      >
        {/* Nav links */}
        <ul className="mobile-links">
          {navItems.map(item => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `mobile-link ${isActive ? 'active' : ''}`}
                onClick={closeMenu}
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Footer: lang + theme */}
        <div className="mobile-menu-footer">
          <div className="mobile-lang-row">
            <button
              className={`mobile-lang-btn ${lang === 'en' ? 'active' : ''}`}
              onClick={() => { switchLang('en'); }}
            >
              🇺🇸 EN
            </button>
            <button
              className={`mobile-lang-btn ${lang === 'hi' ? 'active' : ''}`}
              onClick={() => { switchLang('hi'); }}
            >
              🇮🇳 HI
            </button>
          </div>

          <button className="mobile-theme-btn" onClick={toggleTheme}>
            {theme === 'dark' ? '☀️  Light Mode' : '🌙  Dark Mode'}
          </button>
        </div>
      </div>

      {/* Backdrop overlay */}
      {menuOpen && (
        <div
          className="menu-overlay"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default Navbar;
